import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Mail,
  Phone,
  Tag,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { getAvatarImage } from "../../../lib/getAvatarImage";

import parsePhoneNumber from "libphonenumber-js";
import { Button } from "../../ui/button";

import type { Contact } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardMentionAction,
  BaseEntityCardMetadata,
  BaseEntityCardMetadataItem,
  BaseEntityCardProps,
  BaseEntityCardTitle,
} from "../BaseCard";

export { Contact };

type ContactCardProps = Contact &
  BaseEntityCardProps & {
    custom_properties?: Record<string, string>;
  };

export function ContactCard({
  platform,
  name,
  company,
  email = "",
  phone,
  image_url,
  web_link,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
  custom_properties,
}: ContactCardProps) {
  const [showCustomProperties, setShowCustomProperties] = useState(false);

  const formattedPhone = useMemo(() => {
    if (!phone) return null;
    const parsed = parsePhoneNumber(phone);
    if (!parsed) return null;
    return parsed.formatInternational();
  }, [phone]);

  const hasCustomProperties = useMemo(() => {
    return custom_properties && Object.keys(custom_properties).length > 0;
  }, [custom_properties]);

  const formatPropertyName = (propName: string) => {
    return propName.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <ContactCardAvatar image_url={image_url} name={name} email={email} />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={platform}>{name}</BaseEntityCardTitle>
          <BaseEntityCardActions>
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {web_link && <BaseEntityCardExternalLinkAction href={web_link} />}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          <BaseEntityCardMetadata>
            {company && (
              <BaseEntityCardMetadataItem
                icon={<Building2 className="h-4 w-4" />}
              >
                {company}
              </BaseEntityCardMetadataItem>
            )}
            {email && (
              <BaseEntityCardMetadataItem icon={<Mail className="h-4 w-4" />}>
                {email}
              </BaseEntityCardMetadataItem>
            )}
            {(formattedPhone || phone) && (
              <BaseEntityCardMetadataItem icon={<Phone className="h-4 w-4" />}>
                {formattedPhone || phone}
              </BaseEntityCardMetadataItem>
            )}
          </BaseEntityCardMetadata>

          {/* Collapsible Custom Properties Section */}
          {hasCustomProperties && (
            <div className="flex flex-col gap-1.5 mt-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-5 p-0 text-xs text-muted-foreground hover:text-foreground justify-start -ml-1"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCustomProperties(!showCustomProperties);
                }}
              >
                {showCustomProperties ? (
                  <ChevronDown className="h-3 w-3 mr-1" />
                ) : (
                  <ChevronRight className="h-3 w-3 mr-1" />
                )}
                <Tag className="h-3 w-3 mr-1" />
                {Object.keys(custom_properties!).length} custom{" "}
                {Object.keys(custom_properties!).length === 1
                  ? "property"
                  : "properties"}
              </Button>

              {showCustomProperties && (
                <div className="mt-1 space-y-1 pl-1">
                  {Object.entries(custom_properties!).map(([key, value]) => (
                    <div key={key} className="flex items-start gap-2 text-xs">
                      <span className="text-muted-foreground font-medium min-w-0 truncate">
                        {formatPropertyName(key)}:
                      </span>
                      <span className="text-foreground break-words min-w-0">
                        {value || "—"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}

// Avatar component for ContactCard
export function ContactCardAvatar({
  image_url,
  name,
  email,
  className,
  style,
}: Partial<ContactCardProps>) {
  const [avatarImage, setAvatarImage] = useState<string | null>(null);

  const nameInitials = name
    ? name
        .split(" ")
        .map((n: string) => n[0])
        .join("")
    : "";

  useEffect(() => {
    if (image_url) {
      setAvatarImage(image_url);
    } else if (email) {
      setAvatarImage(getAvatarImage(email));
    }
  }, [email, image_url]);

  return (
    <Avatar className={cn("h-10 w-10 shrink-0", className)} style={style}>
      {avatarImage && <AvatarImage src={avatarImage} />}
      <AvatarFallback>{nameInitials}</AvatarFallback>
    </Avatar>
  );
}
